/**
 * Helper functions for accessing mesh element data.
 *
 * These helpers provide a consistent API to get element data from:
 * - elementIndex (primary source for data model properties)
 * - runtimeMeshState (for runtime-only state like textures, handles)
 */

import * as THREE from 'three';
import {
  getTextureMetadata,
  getHandleMetadata,
  getFixtureMetadata,
  isHighlightOverlay,
  isProceduralGroup,
  isOpeningGroup,
  isInteriorFace,
  isExteriorFace,
  isSelected,
  isDebugObject,
  isStructureObject,
  getObjectMetadata,
  getObjectId,
} from '../state/runtimeMeshState';

/**
 * Get the element type for a mesh.
 *
 * @param {Object} mesh - Three.js mesh object
 * @param {Map} elementIndex - Map of element IDs to {type, data, parentId}
 * @returns {string|undefined} Element type (e.g., 'wall', 'floor', 'door')
 */
export const getMeshElementType = (mesh, elementIndex) => {
  if (!mesh) return undefined;
  return elementIndex?.get(mesh.uuid)?.type;
};

/**
 * Get the full element data for a mesh.
 *
 * @param {Object} mesh - Three.js mesh object
 * @param {Map} elementIndex - Map of element IDs to {type, data, parentId}
 * @returns {Object} Element data object (dimensions, openings, etc.)
 */
export const getMeshElementData = (mesh, elementIndex) => {
  if (!mesh) return {};
  const entry = elementIndex?.get(mesh.uuid);
  if (!entry) return {};
  // Include the element type in the returned data for floor/ceiling detection
  return { ...entry.data, type: entry.type };
};

/**
 * Get the parent element ID for a mesh.
 *
 * @param {Object} mesh - Three.js mesh object
 * @param {Map} elementIndex - Map of element IDs to {type, data, parentId}
 * @returns {string|undefined} Parent element ID
 */
export const getMeshParentId = (mesh, elementIndex) => {
  if (!mesh) return undefined;
  return elementIndex?.get(mesh.uuid)?.parentId;
};

/**
 * Check if a mesh is a texture tile geometry.
 * Uses runtimeMeshState for runtime markers.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {string|undefined} Segment UUID this tile belongs to, or undefined
 */
export const isTextureTile = (mesh) => {
  if (!mesh) return undefined;
  return getTextureMetadata(mesh.uuid)?.tileGeometryFor;
};

/**
 * Get full texture metadata for a mesh.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {Object|undefined} {tileGeometryFor, wallNormal, regionId}
 */
export const getMeshTextureMetadata = (mesh) => {
  if (!mesh) return undefined;
  return getTextureMetadata(mesh.uuid);
};

/**
 * Check if a mesh is a region selection handle.
 * Uses runtimeMeshState for runtime markers.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {boolean} True if mesh is a region handle
 */
export const isRegionHandle = (mesh) => {
  if (!mesh) return false;
  return !!getHandleMetadata(mesh.uuid)?.isRegionHandle;
};

/**
 * Get full handle metadata for a mesh.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {Object|undefined} {regionIndex, handleType, isRegionHandle, isRegionOutline}
 */
export const getMeshHandleMetadata = (mesh) => {
  if (!mesh) return undefined;
  return getHandleMetadata(mesh.uuid);
};

/**
 * Check if a mesh is a light fixture.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {boolean} True if mesh has fixture metadata
 */
export const isFixture = (mesh) => {
  if (!mesh) return false;
  return !!getFixtureMetadata(mesh.uuid);
};

/**
 * Get fixture metadata for a mesh.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {Object|undefined} {fixtureId, originalIntensity, isModified}
 */
export const getMeshFixtureMetadata = (mesh) => {
  if (!mesh) return undefined;
  return getFixtureMetadata(mesh.uuid);
};

/**
 * Check if a mesh is a highlight overlay.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {boolean} True if mesh is a highlight overlay
 */
export const isMeshHighlightOverlay = (mesh) => {
  if (!mesh) return false;
  return isHighlightOverlay(mesh.uuid);
};

/**
 * Check if a mesh/group is a procedural group (for raycast traversal).
 *
 * @param {Object} mesh - Three.js object
 * @returns {boolean} True if object is a procedural group
 */
export const isMeshProceduralGroup = (mesh) => {
  if (!mesh) return false;
  return isProceduralGroup(mesh.uuid);
};

/**
 * Check if a mesh/group is an opening group.
 *
 * @param {Object} mesh - Three.js object
 * @returns {boolean} True if object is an opening group
 */
export const isMeshOpeningGroup = (mesh) => {
  if (!mesh) return false;
  return isOpeningGroup(mesh.uuid);
};

/**
 * Check if a mesh is an interior wall face.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {boolean} True if mesh is an interior face
 */
export const isMeshInteriorFace = (mesh) => {
  if (!mesh) return false;
  return isInteriorFace(mesh.uuid);
};

/**
 * Check if a mesh is an exterior wall face.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {boolean} True if mesh is an exterior face
 */
export const isMeshExteriorFace = (mesh) => {
  if (!mesh) return false;
  return isExteriorFace(mesh.uuid);
};

/**
 * Check if a mesh is currently selected.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {boolean} True if mesh is selected
 */
export const isMeshSelected = (mesh) => {
  if (!mesh) return false;
  return isSelected(mesh.uuid);
};

/**
 * Check if a mesh should be skipped during raycast.
 * Combines multiple runtime state checks.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {boolean} True if mesh should be skipped
 */
export const shouldSkipInRaycast = (mesh) => {
  if (!mesh) return false;
  return (
    isMeshHighlightOverlay(mesh) || isRegionHandle(mesh) || isTextureTile(mesh)
  );
};

/**
 * Get element dimensions from elementIndex or userData.
 *
 * @param {Object} mesh - Three.js mesh object
 * @param {Map} elementIndex - Map of element IDs to {type, data, parentId}
 * @returns {Array|undefined} Dimensions array [width, height, depth]
 */
export const getMeshDimensions = (mesh, elementIndex) => {
  const data = getMeshElementData(mesh, elementIndex);
  return data.dimensions;
};

/**
 * Get wall ID from mesh.
 *
 * @param {Object} mesh - Three.js mesh object
 * @param {Map} elementIndex - Map of element IDs to {type, data, parentId}
 * @returns {string|undefined} Wall ID
 */
export const getMeshWallId = (mesh, elementIndex) => {
  const data = getMeshElementData(mesh, elementIndex);
  return data.wallId || data.id;
};

/**
 * Get floor ID from mesh.
 *
 * @param {Object} mesh - Three.js mesh object
 * @param {Map} elementIndex - Map of element IDs to {type, data, parentId}
 * @returns {string|undefined} Floor ID
 */
export const getMeshFloorId = (mesh, elementIndex) => {
  const data = getMeshElementData(mesh, elementIndex);
  return data.floorId || data.id;
};

/**
 * Check if a mesh is a debug object (for cleanup).
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {boolean} True if mesh is a debug object
 */
export const isMeshDebugObject = (mesh) => {
  if (!mesh) return false;
  return isDebugObject(mesh.uuid);
};

/**
 * Check if a mesh is a structure object (for PreTransformedViewer cleanup).
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {boolean} True if mesh is a structure object
 */
export const isMeshStructureObject = (mesh) => {
  if (!mesh) return false;
  return isStructureObject(mesh.uuid);
};

/**
 * Get object runtime metadata for a mesh.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {Object|undefined} {id, dimensions, currentDimensions, originalDimensions, originalObjectData}
 */
export const getMeshObjectMetadata = (mesh) => {
  if (!mesh) return undefined;
  return getObjectMetadata(mesh.uuid);
};

/**
 * Get object ID from runtime metadata.
 *
 * @param {Object} mesh - Three.js mesh object
 * @returns {string|undefined} Object ID
 */
export const getMeshObjectId = (mesh) => {
  if (!mesh) return undefined;
  return getObjectId(mesh.uuid);
};

/**
 * Get wall coordinate system vectors.
 * Uses wallNormal from wall data (Firestore), falls back to geometry.
 * wallRight and wallUp are derived from wallNormal.
 *
 * This ensures consistent coordinate systems across:
 * - PolygonWalls.jsx (UV generation)
 * - WallRegionHandles.js (region percentage calculations)
 * - textureGeometry.js (fallback UV generation)
 *
 * @param {Object} wallData - Wall data containing wallNormal (from elementIndex or direct)
 * @param {THREE.BufferGeometry} geometry - Wall geometry (fallback)
 * @returns {Object} { wallNormal, wallRight, wallUp } - all THREE.Vector3
 */
export const getWallCoordinateSystem = (wallData = null, geometry = null) => {
  let wallRight = null;
  let wallNormal = null;
  const wallUp = new THREE.Vector3(0, 1, 0);

  // FIRST: Check if this is a floor or ceiling (horizontal surface)
  // Must check this BEFORE rotation.y, because floors also have rotation fields
  if (wallData?.type === 'floor' || wallData?.type === 'ceiling') {
    const isFloor = wallData.type === 'floor';
    return {
      wallNormal: new THREE.Vector3(0, isFloor ? 1 : -1, 0),
      wallRight: new THREE.Vector3(1, 0, 0),
      wallUp: new THREE.Vector3(0, 0, isFloor ? 1 : -1),
    };
  }

  // Primary: compute from rotation.y (most common in Firestore data for WALLS)
  if (wallData?.rotation?.y !== undefined) {
    // Default wall faces +Z, rotation.y rotates around Y axis
    const angle = wallData.rotation.y;
    // wallNormal = rotated +Z direction
    wallNormal = new THREE.Vector3(Math.sin(angle), 0, Math.cos(angle));
    // wallRight = 90° CCW rotation of wallNormal
    wallRight = new THREE.Vector3(wallNormal.z, 0, -wallNormal.x);
  } else if (wallData?.direction || wallData?.wallDir) {
    // Use explicit direction if available
    const dir = wallData.direction || wallData.wallDir;
    wallRight = new THREE.Vector3(dir.x, 0, dir.z).normalize();
    // wallNormal = 90° CCW rotation of wallRight around Y
    wallNormal = new THREE.Vector3(-wallRight.z, 0, wallRight.x);
  } else if (wallData?.normal || wallData?.perpDir) {
    // Derive direction from normal/perpDir (perpendicular to wall)
    // normal = 90° CCW rotation of direction, so reverse it:
    // direction.x = normal.z, direction.z = -normal.x
    const n = wallData.normal || wallData.perpDir;
    wallRight = new THREE.Vector3(n.z, 0, -n.x).normalize();
    wallNormal = new THREE.Vector3(n.x, 0, n.z).normalize();
  } else if (wallData && Object.keys(wallData).length > 0) {
    console.warn(
      'getWallCoordinateSystem: wallData missing rotation, direction/wallDir, and normal/perpDir, falling back to geometry',
      { wallData }
    );
  }

  // Fallback: compute from geometry vertex normals
  if (!wallRight && geometry) {
    console.warn(
      'getWallCoordinateSystem: no direction available, falling back to geometry (may flip for walls with openings)'
    );
    geometry.computeVertexNormals();
    const normalAttr = geometry.attributes.normal;
    if (normalAttr && normalAttr.count > 0) {
      wallNormal = new THREE.Vector3(
        normalAttr.getX(0),
        normalAttr.getY(0),
        normalAttr.getZ(0)
      ).normalize();
      wallRight = new THREE.Vector3()
        .crossVectors(wallUp, wallNormal)
        .normalize();
    }
  }

  if (!wallRight || !wallNormal) return null;

  // Handle horizontal surfaces (floors/ceilings) where normal is mostly vertical
  // In this case, crossVectors(wallUp, wallNormal) would be degenerate (zero vector)
  // Use X as "right" and Z as "up" for horizontal surfaces
  if (Math.abs(wallNormal.y) > 0.9) {
    const isFloor = wallNormal.y > 0; // Normal pointing up = floor
    return {
      wallNormal,
      wallRight: new THREE.Vector3(1, 0, 0),
      wallUp: new THREE.Vector3(0, 0, isFloor ? 1 : -1), // Flip Z for ceilings
    };
  }

  return { wallNormal, wallRight, wallUp };
};
